// src/components/tokenPage/performance/TokenStreaksAndEfficiencyCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useTokenPrice } from "@/contexts/token";
import {
  sliceByRange,
  toReturns,
  calculateStreaks,
  distanceFromHigh,
  daysSinceHigh,
  cagr,
  maxDrawdown,
  calmarRatio,
} from "@/lib/metrics";

type RangeKey = '90d' | '180d' | '365d' | 'All';

/**
 * Performance Consistency Card
 * 
 * Shows streaks, trends and efficiency metrics.
 * All calculations are done via lib/metrics.
 */
const PerformanceConsistencyCard: React.FC = () => {
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;
  const [range, setRange] = useState<RangeKey>('180d');

  const metrics = useMemo(() => {
    if (prices.values.length === 0) {
      return null;
    }

    // Slice by range using price dates
    const targetLen = range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : prices.values.length;
    const { values: priceSlice } = sliceByRange(prices.dates, prices.values, targetLen);

    // Convert prices to returns for MAR calculation
    const returnsSlice = toReturns(priceSlice);

    // Use lib/metrics functions for all calculations
    const streaks = calculateStreaks(priceSlice);
    const distFromHigh = distanceFromHigh(priceSlice);
    const daysSinceATH = daysSinceHigh(priceSlice);
    const annReturn = cagr(priceSlice);
    const maxDD = maxDrawdown(priceSlice);
    const mar = calmarRatio(returnsSlice); // MAR = Ann Return / Max DD (using returns)

    return {
      currStreak: streaks?.currentStreak ?? null,
      longestUp: streaks?.longestUpStreak ?? null,
      longestDown: streaks?.longestDownStreak ?? null,
      distFromHighPct: distFromHigh,
      daysSinceHigh: daysSinceATH,
      cagrPct: annReturn !== null ? annReturn * 100 : null,
      maxDDPct: maxDD !== null ? maxDD * 100 : null,
      mar,
    };
  }, [prices.values, prices.dates, range]);

  // Hide if no data available
  if (!isLoading && !metrics) {
    return null;
  }

  const hasData = metrics && (
    metrics.currStreak != null || metrics.longestUp != null ||
    metrics.longestDown != null || metrics.distFromHighPct != null ||
    metrics.daysSinceHigh != null || metrics.cagrPct != null ||
    metrics.maxDDPct != null || metrics.mar != null
  );

  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Performance Consistency"
      subtitle="Streaks, trends and efficiency metrics"
      icon="bar-chart-2"
      iconColor="text-violet-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker
          options={['90d', '180d', '365d', 'All'] as const}
          value={range}
          onChange={setRange}
          theme="blue"
        />
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <MetricBox label="Current Streak (days)" value={metrics?.currStreak ?? null} fmt="int" toneBySign />
        <MetricBox label="Longest Up (days)" value={metrics?.longestUp ?? null} fmt="int" />
        <MetricBox label="Longest Down (days)" value={metrics?.longestDown ?? null} fmt="int" />
        <MetricBox label="Dist from period high" value={metrics?.distFromHighPct ?? null} fmt="pct1" toneBySign />
        <MetricBox label="Days since period high" value={metrics?.daysSinceHigh ?? null} fmt="int" />
        <MetricBox label="Ann. Return (CAGR)" value={metrics?.cagrPct ?? null} fmt="pct1" toneBySign />
        <MetricBox label="Max Drawdown" value={metrics?.maxDDPct ?? null} fmt="pct1" toneBySign />
        <MetricBox label="MAR (Calmar)" value={metrics?.mar ?? null} fmt="ratio2" toneBySign />
      </div>
    </DataCard>
  );
};

export default PerformanceConsistencyCard;
